import React, { createContext, useContext, useMemo, useEffect, useState, useRef } from 'react';
import WebSocketManager from './WebSocketManager';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const manager = useMemo(() => new WebSocketManager(), []);
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    totalFiles: 0,
    processedFiles: 0,
    successful: 0,
    duplicates: 0,
    failed: 0,
    overallProgress: 0,
    fileStatuses: {},
  });
  const uniqueIdRef = useRef(`${Math.random()}-${Date.now()}`);

  const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const url =
    window.location.hostname === 'localhost'
      ? `${wsProtocol}://localhost:8081`
      : `${wsProtocol}://${window.location.host}/ws/`;

  const decodeURIComponentSafe = (str) => {
    try {
      return decodeURIComponent(str);
    } catch {
      return str;
    }
  };

  useEffect(() => {
    const handleWebSocketMessage = (msg) => {
      const data = JSON.parse(msg.data);
      setMessages((prev) => [...prev, data]);
      if (data.type === 'upload_start') {
        setUploadProgress({
          totalFiles: data.totalFiles,
          processedFiles: 0,
          successful: 0,
          duplicates: 0,
          failed: 0,
          overallProgress: 0,
          fileStatuses: {},
        });
      } else if (data.type === 'upload_progress' && data.filename) {
        const FINAL_STATUSES = ['success', 'duplicate', 'error', 'failed'];

        setUploadProgress(prev => {
          const cleanFileName = decodeURIComponentSafe(data.filename);
          const prevStatus = prev.fileStatuses[cleanFileName]?.status;
          const isFinal = FINAL_STATUSES.includes(data.status);

          const newFileStatuses = {
            ...prev.fileStatuses,
            [cleanFileName]: {
              status: data.status,
              message: data.message,
              progress: data.progress || 100,
            },
          };

          let newSuccessful = prev.successful;
          let newDuplicates = prev.duplicates;
          let newFailed = prev.failed;
          let newProcessedFiles = prev.processedFiles;

          if (isFinal && !FINAL_STATUSES.includes(prevStatus)) {
            newProcessedFiles++;
            if (data.status === 'success') newSuccessful++;
            else if (data.status === 'duplicate') newDuplicates++;
            else if (data.status === 'error' || data.status === 'failed') newFailed++;
          }

          const newOverallProgress =
            prev.totalFiles > 0
              ? ((newSuccessful + newDuplicates + newFailed) / prev.totalFiles) * 100
              : 0;

          return {
            ...prev,
            processedFiles: newProcessedFiles,
            successful: newSuccessful,
            duplicates: newDuplicates,
            failed: newFailed,
            overallProgress: newOverallProgress,
            fileStatuses: newFileStatuses,
          };
        });

      } else if (data.type === 'upload_complete') {
        setUploadProgress(prev => ({
          ...prev,
          overallProgress: 100,
        }));
      }
    };

    const config = {
      onOpen: () => setIsConnected(true),
      onClose: () => setIsConnected(false),
      onMessage: handleWebSocketMessage,
    };

    const currentId = uniqueIdRef.current;
    manager.connect(currentId, url, config);

    return () => {
      manager.disconnect(currentId);
    };
  }, [manager, url]);

  return (
    <WebSocketContext.Provider value={{ manager, messages, isConnected, uploadProgress }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
